import { NGUONG_NHAC_SINH_LAI, canBuocHai, loginInputSchema } from '@qlk/shared';

import { useId, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useDangNhap, useDangNhapBuocHai, useToi } from '../../../lib/auth.js';
import { Nut } from '../../../ui/index.js';

export function LoginPage() {
  const idEmail = useId();
  const idMatKhau = useId();
  const [email, setEmail] = useState('');
  const [matKhau, setMatKhau] = useState('');
  const [loiForm, setLoiForm] = useState<string | null>(null);

  const dangNhap = useDangNhap();
  const toi = useToi();
  const dieuHuong = useNavigate();
  const viTri = useLocation();

  const quayVe = (viTri.state as { tuDau?: string } | null)?.tuDau ?? '/';

  /** Bước hai: phiếu của bước một, và số mã dự phòng còn lại để nhắc đúng lúc. */
  const [buocHai, setBuocHai] = useState<{ phieu: string; maDuPhongConLai: number } | null>(null);

  if (toi.data) return <Navigate to={quayVe} replace />;

  const guiForm = (e: FormEvent) => {
    e.preventDefault();
    const kiemTra = loginInputSchema.safeParse({ email, password: matKhau });
    if (!kiemTra.success) {
      setLoiForm(kiemTra.error.issues[0]?.message ?? 'Dữ liệu không hợp lệ');
      return;
    }
    setLoiForm(null);
    dangNhap.mutate(kiemTra.data, {
      onSuccess: (du) => {
        /*
         * Mật khẩu đúng nhưng tài khoản đã bật 2FA ⇒ lượt này **chưa** có
         * cookie phiên nào, chỉ có một phiếu 5 phút. Đổi form, không điều
         * hướng.
         */
        if (canBuocHai(du)) {
          setBuocHai({ phieu: du.phieu, maDuPhongConLai: du.maDuPhongConLai });
          setMatKhau('');
          return;
        }
        void dieuHuong(quayVe, { replace: true });
      },
    });
  };

  const thongBaoLoi = loiForm ?? (dangNhap.error ? dangNhap.error.message : null);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-nen px-4 py-10">
      <div className="w-full max-w-[420px]">
        <div className="mb-6 flex items-center justify-center gap-3">
          {/*
            Ô tối bo góc, **đúng nền `#0F172A` của icon trình duyệt**: logo vàng
            `#F3D45B` đặt thẳng lên nền sáng `bg-nen` thì tương phản quá thấp,
            nhạt đi như một hình mờ. Logo không có chữ, nên dòng tên công ty
            bên cạnh vẫn cần.
          */}
          <span
            data-logo
            className="inline-flex size-14 shrink-0 items-center justify-center rounded-xl bg-menu-nen p-2"
          >
            <img
              src="/brand/logo.png"
              alt="WM Media"
              width={300}
              height={300}
              className="size-full object-contain"
            />
          </span>
          <span>
            <span className="block text-tieu-de-khoi leading-tight font-bold text-chu">
              WM Media
            </span>
            <span className="block text-phu text-chu-mo">Quản lý kênh YouTube</span>
          </span>
        </div>

        {buocHai ? (
          <FormBuocHai
            phieu={buocHai.phieu}
            maDuPhongConLai={buocHai.maDuPhongConLai}
            quayVe={quayVe}
          />
        ) : (
          <form
            onSubmit={guiForm}
            noValidate
            className="rounded-the border border-vien bg-be-mat p-6"
          >
            <h1 className="text-so-lon font-bold text-chu">Đăng nhập</h1>
            <p className="mt-1 mb-5 text-co-ban text-chu-phu">Dùng tài khoản nội bộ của công ty.</p>

            <label htmlFor={idEmail} className="mb-1 block text-co-ban font-medium text-chu-phu">
              Email
            </label>
            <input
              id={idEmail}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              className="vung-bam mb-4 w-full rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu"
            />

            <label htmlFor={idMatKhau} className="mb-1 block text-co-ban font-medium text-chu-phu">
              Mật khẩu
            </label>
            <input
              id={idMatKhau}
              type="password"
              autoComplete="current-password"
              value={matKhau}
              onChange={(e) => {
                setMatKhau(e.target.value);
              }}
              className="vung-bam w-full rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu"
            />

            {thongBaoLoi && (
              <p
                role="alert"
                className="mt-4 rounded-[10px] bg-bad-nen px-3 py-2 text-co-ban text-bad-chu"
              >
                {thongBaoLoi}
              </p>
            )}

            <Nut type="submit" kieu="chinh" dangChay={dangNhap.isPending} className="mt-5 w-full">
              Đăng nhập
            </Nut>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Bước hai của lượt đăng nhập.
 *
 * **Một ô** cho cả mã 6 số và mã dự phòng: người vừa mất điện thoại không phải
 * đi tìm xem gõ mã dự phòng vào đâu. Máy chủ phân loại theo hình dạng.
 */
function FormBuocHai({
  phieu,
  maDuPhongConLai,
  quayVe,
}: {
  phieu: string;
  maDuPhongConLai: number;
  quayVe: string;
}) {
  const idMa = useId();
  const [ma, setMa] = useState('');
  const buocHai = useDangNhapBuocHai();
  const dieuHuong = useNavigate();

  const gui = (e: FormEvent) => {
    e.preventDefault();
    if (ma.trim().length < 6) return;
    buocHai.mutate(
      { phieu, ma },
      {
        onSuccess: () => {
          void dieuHuong(quayVe, { replace: true });
        },
      },
    );
  };

  return (
    <form onSubmit={gui} noValidate className="rounded-the border border-vien bg-be-mat p-6">
      <h1 className="text-so-lon font-bold text-chu">Xác thực hai bước</h1>
      <p className="mt-1 mb-5 text-co-ban text-chu-phu">
        Mở app xác thực trên điện thoại và nhập mã 6 số. Mất điện thoại thì nhập một{' '}
        <strong className="font-medium text-chu">mã dự phòng</strong>.
      </p>

      <label htmlFor={idMa} className="mb-1 block text-co-ban font-medium text-chu-phu">
        Mã xác thực
      </label>
      <input
        id={idMa}
        value={ma}
        autoComplete="one-time-code"
        inputMode="text"
        autoFocus
        onChange={(e) => {
          setMa(e.target.value);
        }}
        className="vung-bam w-full rounded-[10px] border border-vien bg-be-mat px-3 font-mono text-co-ban tracking-wider text-chu"
      />

      {buocHai.isError && (
        <p
          role="alert"
          className="mt-4 rounded-[10px] bg-bad-nen px-3 py-2 text-co-ban text-bad-chu"
        >
          {buocHai.error.message}
        </p>
      )}

      {/* Nhắc cạn mã dự phòng ở đúng chỗ người dùng đang cần tới chúng. */}
      {maDuPhongConLai <= NGUONG_NHAC_SINH_LAI && (
        <p className="mt-4 rounded-[10px] bg-warn-nen px-3 py-2 text-phu text-warn-chu">
          Còn {String(maDuPhongConLai)} mã dự phòng. Vào Tài khoản của tôi để sinh bộ mới sau khi
          đăng nhập.
        </p>
      )}

      <Nut
        type="submit"
        kieu="chinh"
        disabled={ma.trim().length < 6}
        dangChay={buocHai.isPending}
        className="mt-5 w-full"
      >
        Xác nhận
      </Nut>
    </form>
  );
}
